<template>
	<div v-if="categoryTabs" class="budget-creator-wrapper">
		<h3
			class="budget-counter body-text-1"
			:class="{ error: noBudgetLeft }"
			:aria-label="$t('householdNMIAbbr', { amount: maxBudgetRemaining })"
		>
			{{ $t('householdNMI') }}:
			<strong>{{ maxBudgetRemaining | currency }}</strong>
		</h3>
		<h3
			class="budget-counter body-text-1"
			:class="{ error: noSavingsLeft }"
			:aria-label="$t('savingsAvailableAbbr', { amount: maxSavingsRemaining })"
		>
			{{
				$t('savingsAvailable', {
					percentageOfNmi: percentageOfNmiSavings
				})
			}}
			<strong>{{ maxSavingsRemaining | currency }}</strong>
		</h3>
		<div class="budget-creator" :class="{ ireland: this.$store.getters.isIrl }">
			<!-- Info post-secondary education loan debt-->
			<div
				v-if="showPostSecondary"
				class="category-cnt education-loan"
				:class="{ expanded: educationLoan.expanded }"
			>
				<button
					class="category-header"
					tabindex="0"
					:aria-label="
						$t('budgetSection', {
							category: educationLoan.name,
							amount: educationLoan.loanTotal
						})
					"
					role="button"
					:aria-expanded="(!!educationLoan.expanded).toString()"
					@click="toggleSliders($event.target, educationLoan)"
					@mousedown.prevent
				>
					<div class="heading-3" aria-hidden="true">
						{{ educationLoan.name }}
						<span v-show="false" class="text--small text--danger">
							{{ $t('invalidBudgetOption') }}
						</span>
					</div>
					<svgicon name="arrow-right"></svgicon>
				</button>
				<transition name="slide" mode="out-in">
					<div v-show="educationLoan.expanded" class="slider-wrapper">
						<div class="slider-info student">
							<div class="legend">
								<div class="legend-color"></div>
								<label
									class="body-text-2 slider-name"
									v-html="
										$t('loanTotal', {
											amount: educationLoan.loanTotal
										})
									"
								>
								</label>
							</div>
							<div class="legend">
								<div class="legend-color"></div>
								<label
									class="body-text-2 slider-name"
									v-html="
										$t('outstandingAmount', {
											amount: educationLoan.outstandingAmount
										})
									"
								></label>
							</div>
							<div class="legend">
								<div class="legend-color"></div>
								<label
									class="body-text-2 slider-name"
									v-html="
										$t('minimumPayment', {
											amount: educationLoan.minimumPayment
										})
									"
								></label>
							</div>
						</div>
					</div>
				</transition>
			</div>
			<!-- Info credit card-->
			<div
				v-if="showCreditCard"
				class="category-cnt education-loan"
				:class="{ expanded: creditCard.expanded }"
			>
				<button
					class="category-header"
					tabindex="0"
					:aria-label="
						$t('budgetSection', {
							category: creditCard.name,
							amount: creditCard.loanTotal
						})
					"
					role="button"
					:aria-expanded="(!!creditCard.expanded).toString()"
					@click="toggleSliders($event.target, creditCard)"
					@mousedown.prevent
				>
					<div class="heading-3" aria-hidden="true">
						{{ creditCard.name }}
						<span v-show="false" class="text--small text--danger">
							{{ $t('invalidBudgetOption') }}
						</span>
					</div>
					<svgicon name="arrow-right"></svgicon>
				</button>
				<transition name="slide" mode="out-in">
					<div v-show="creditCard.expanded" class="slider-wrapper">
						<div class="slider-info student">
							<div class="legend">
								<div class="legend-color"></div>
								<label
									class="body-text-2 slider-name"
									v-html="$t('creditLimit') + ': ' + creditCard.creditLimit"
								>
								</label>
							</div>
							<div class="legend">
								<div class="legend-color"></div>
								<label
									class="body-text-2 slider-name"
									v-html="
										$t('remainingBalance') + ': ' + creditCard.remainingBalance
									"
								></label>
							</div>
							<div class="legend">
								<div class="legend-color"></div>
								<label
									class="body-text-2 slider-name"
									v-html="
										$t('minimumPaymentDue') + ': ' + creditCard.minimumPayment
									"
								></label>
							</div>
						</div>
					</div>
				</transition>
			</div>

			<!-- Savings Budget creator -->
			<div class="category-cnt savings" :class="{ expanded: savings.expanded }">
				<button
					class="category-header"
					tabindex="0"
					:aria-label="
						$t('budgetSection', {
							category: savings.name,
							amount: savings.amount
						})
					"
					role="button"
					:aria-expanded="(!!savings.expanded).toString()"
					@mousedown.prevent
					@click="toggleSliders($event.target, savings)"
				>
					<div class="heading-3" aria-hidden="true">
						{{ savings.name }}
						<span
							v-show="!hasValidOptions(savings)"
							class="text--small text--danger"
						>
							{{ $t('invalidBudgetOption') }}
						</span>
					</div>
					<div class="heading-4" aria-hidden="true">
						{{ savings.amount | currency }}
					</div>
					<svgicon name="arrow-right"></svgicon>
				</button>
				<transition name="slide" mode="out-in">
					<div
						v-if="savings.expanded !== null"
						v-show="savings.expanded"
						class="sliders-cnt"
					>
						<div class="legend-wrapper">
							<ja-budget-slider-legend></ja-budget-slider-legend>
						</div>
						<div
							v-for="(subcategory, i) in savingsSubcategories"
							:key="i"
							class="slider-wrapper"
						>
							<div class="slider-info">
								<div class="slider-info-title">
									<label class="heading-4 slider-name">
										{{ $t(subcategory.name) }}
									</label>
									<div v-if="subcategory.description" class="tooltip-wrapper">
										<button
											class="tooltip-button"
											:aria-label="$t('moreInfoSavings')"
											@click="savingsGoalTooltip(i)"
											@mousedown.prevent
										>
											<svgicon name="savings-info" :original="true"> </svgicon>
										</button>
										<ja-tooltip
											v-show="i === selectedSubcategoryIndex && showTooltipDesc"
											role="status"
											class="left"
											width-msg="435px"
											>{{ $t(subcategory.description) }}</ja-tooltip
										>
									</div>
								</div>
								<div
									v-if="subcategory.hasOwnProperty('totalAmount')"
									class="saving-amount-wrapper"
								>
									<ja-currency-input
										v-model="subcategory.totalAmount"
										:aria-label="
											$t('totalGoalAmount', { category: subcategory.name })
										"
										:placeholder="$t('totalAmount')"
										:class="validationClasses(subcategory.totalAmount)"
										@input="announceTotalSavings(subcategory)"
									>
									</ja-currency-input>
								</div>

								<div class="body-text-2">{{ $t('monthlyAmount') }}</div>
								<div
									v-if="!subcategory.hasOwnProperty('totalAmount')"
									class="tooltip-wrapper"
								>
									<button
										class="tooltip-button"
										:aria-label="$t('moreInfoSavings')"
										@click="toggleTooltip"
										@mousedown.prevent
									>
										<svgicon
											v-if="!subcategory.hasOwnProperty('totalAmount')"
											name="savings-info"
											:original="true"
										>
										</svgicon>
									</button>
									<ja-tooltip v-show="showTooltip" role="status" class="left">{{
										$t('savingsTooltip')
									}}</ja-tooltip>
								</div>
							</div>
							<div class="budget-slider-wrapper">
								<ja-budget-slider
									v-model="subcategory.amount"
									:budget="maxSavingsRemaining"
									:name="subcategory.name"
									:min="0"
									:max="getMaxSliderValue(percentageOfNmiDividedByHundred)"
									:recommended-min="subcategory.minimum"
									:recommended-max="subcategory.maximum"
									:is-limit-value="noSavingsLeft"
									:limit-value="
										noSavingsLeft
											? savingsSubcategoriesMax[i].amount
											: subcategory.amount
									"
									:step="1"
									class="budget-slider"
									@input="onSavingSliderChanged(subcategory)"
								>
								</ja-budget-slider>
								<ja-currency-input
									v-model="subcategory.amount"
									:aria-label="
										$t('amountSaveCategory', { category: subcategory.name })
									"
									class="budget-input"
									:class="validationClasses(subcategory.amount)"
									:max="getMaxSliderValue(percentageOfNmiDividedByHundred)"
									:aria-value-max="netMonthlyIncome"
									:is-disabled="true"
									@input="onSavingSliderChanged(subcategory)"
								>
								</ja-currency-input>
							</div>
							<div
								v-show="subcategory.amount < subcategory.minimum"
								class="msg min-msg"
							>
								{{ $t('sliderMinMsg') }}
							</div>
							<div
								v-show="subcategory.amount > subcategory.maximum"
								class="msg max-msg"
							>
								{{ $t('sliderMaxMsg') }}
							</div>
						</div>
					</div>
				</transition>
			</div>

			<!-- Debts Budget creator -->
			<div
				v-if="debts.subcategories.length"
				class="category-cnt debts"
				:class="{ expanded: debts.expanded }"
			>
				<button
					class="category-header"
					tabindex="0"
					:aria-label="
						$t('budgetSection', { category: debts.name, amount: debts.amount })
					"
					role="button"
					:aria-expanded="(!!debts.expanded).toString()"
					@click="toggleSliders($event.target, debts)"
					@mousedown.prevent
				>
					<div class="heading-3" aria-hidden="true">
						{{ debts.name }}
						<span
							v-show="!hasValidOptions(debts)"
							class="text--small text--danger"
						>
							{{ $t('invalidBudgetOption') }}
						</span>
					</div>
					<div class="heading-4" aria-hidden="true">
						{{ debts.amount | currency }}
					</div>
					<svgicon name="arrow-right"></svgicon>
				</button>
				<transition name="slide" mode="out-in">
					<div
						v-if="debts.expanded"
						v-show="debts.expanded"
						class="sliders-cnt"
					>
						<div class="legend-wrapper">
							<ja-budget-slider-legend></ja-budget-slider-legend>
						</div>
						<div
							v-for="(subcategory, i) in debts.subcategories"
							:key="i"
							class="slider-wrapper"
						>
							<div class="slider-info">
								<label class="heading-4 slider-name">{{
									$t(subcategory.name)
								}}</label>
								<div class="body-text-2">{{ $t('monthlyAmount') }}</div>
							</div>
							<div class="budget-slider-wrapper">
								<ja-budget-slider
									v-model="subcategory.budget"
									:budget="maxBudgetRemaining"
									:name="subcategory.name"
									:min="0"
									:max="getMaxSliderValue(percentageOfNmiDividedByHundred)"
									:recommended-min="subcategory.minimum"
									:recommended-max="subcategory.maximum"
									:step="1"
									class="budget-slider"
									@input="onDebtSliderChanged(subcategory)"
								>
								</ja-budget-slider>
								<ja-currency-input
									v-model="subcategory.budget"
									:aria-label="
										$t('amountSpentCategory', { category: subcategory.name })
									"
									class="budget-input"
									:class="validationClasses(subcategory.budget)"
									:max="getMaxSliderValue(percentageOfNmiDividedByHundred)"
									:aria-value-max="netMonthlyIncome"
									@input="onDebtSliderChanged(subcategory)"
								>
								</ja-currency-input>
							</div>
							<div
								v-show="subcategory.budget < subcategory.minimum"
								class="msg min-msg"
							>
								{{ $t('sliderMinMsg') }}
							</div>
							<div
								v-show="subcategory.budget > subcategory.maximum"
								class="msg max-msg"
							>
								{{ $t('sliderMaxMsg') }}
							</div>
						</div>
					</div>
				</transition>
			</div>
		</div>
		<div class="button-wrapper">
			<ja-button
				ref="button"
				class="small custom_left"
				name="prevLifeScenario"
				@click.native="goPrevStep"
			>
				<svgicon name="arrow-left"></svgicon>
				{{ $t('back') }}
			</ja-button>
			<slot></slot>
			<ja-button
				ref="button"
				class="small"
				name="nextLifeScenario"
				:aria-disabled="isButtonDisabled"
				:disabled="isButtonDisabled"
				:aria-label="$t('saveYourBudgets')"
				@click.native="checkAndSaveBudget"
			>
				{{ $t('finish') }}
				<svgicon name="arrow-right"></svgicon>
			</ja-button>
		</div>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import '../icons/arrow-right';
import '../icons/savings-info';
import UserData from '../../mixins/UserData';
import LifeStageData from '../../mixins/LifeStageData';
import ProfileData from '../../mixins/ProfileData';
import BudgetSlider from './elements/BudgetSlider';
import BudgetSliderLegend from './elements/BudgetSliderLegend';
import Button from '../ui/Button';
import CurrencyInput from '../ui/CurrencyInput';
import Tooltip from '../ui/Tooltip';
import ExpenseCategories from '../../mixins/ExpenseCategories';
import Savings from '../../mixins/Savings';
import Debts from '../../mixins/Debts';
import Accessibility from '../../mixins/Accessibility';
import StudentsService from '@/services/StudentsService';
import {
	numberPercentage,
	lifeStageOrders,
	debtNames,
	debtTypes
} from '@/helpers/constants';

export default {
	components: {
		jaBudgetSlider: BudgetSlider,
		jaButton: Button,
		jaCurrencyInput: CurrencyInput,
		jaTooltip: Tooltip,
		jaBudgetSliderLegend: BudgetSliderLegend
	},
	mixins: [
		UserData,
		LifeStageData,
		ProfileData,
		ExpenseCategories,
		Savings,
		Debts,
		Accessibility
	],
	data() {
		// debugger;
		return {
			savings: {
				expanded: null,
				name: this.$t('savings'),
				amount: 0,
				subcategories: []
			},
			debts: {
				expanded: null,
				name: this.$t('debts'),
				amount: 0,
				subcategories: []
			},
			educationLoan: {
				expanded: null,
				name: this.$t('postLoans'),
				loanTotal: 0,
				outstandingAmount: null,
				minimumPayment: null
			},
			creditCard: {
				expanded: null,
				name: this.$t('creditCard'),
				remainingBalance: null,
				creditLimit: null,
				minimumPayment: null
			},
			showTooltip: false,
			requestSent: false,
			useValidations: false,
			budgetCheckData: null,
			selectedSubcategoryIndex: -1,
			showTooltipDesc: false,
			cmsGoals: [],
			cmsUpperPaymentMethods: [],
			showPostSecondary: false,
			showCreditCard: false,
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl,
			savingsSubcategoriesMax: []
		};
	},
	computed: {
		totalCategoriesBudget() {
			return this.categoryTabs.reduce((sum, item) => {
				if (item.budget >= 0) {
					return sum + parseInt(item.budget);
				}
			}, 0);
		},
		maxBudgetRemaining() {
			return (
				this.netMonthlyIncome -
				this.totalCategoriesBudget -
				this.savings.amount -
				this.debts.amount
			);
		},
		maxSavingsRemaining() {
			let maxAllowedSavings =
				this.netMonthlyIncome * this.percentageOfNmiDividedByHundred;

			if (this.savings.amount > 0 && maxAllowedSavings > 0) {
				maxAllowedSavings = maxAllowedSavings - this.savings.amount;
			}

			if (maxAllowedSavings < 0) {
				return 0;
			}

			return maxAllowedSavings;
		},
		noBudgetLeft() {
			return this.maxBudgetRemaining < 0;
		},
		noSavingsLeft() {
			return this.maxSavingsRemaining === 0;
		},
		isButtonDisabled() {
			return (
				this.noBudgetLeft ||
				this.noSavingsLeft ||
				this.requestSent ||
				!this.areAllBudgetsSet()
			);
		},
		percentageOfNmiDividedByHundred() {
			return this.calculatePercentageOfNmiToSavings();
		},
		percentageOfNmiSavings() {
			return this.percentageOfNmiDividedByHundred * numberPercentage.HUNDRED;
		},
		totalAmount() {
			return this.profileData.totalStudentLoan;
		},
		loanDebtLeft() {
			return this.profileData.studentLoanDebt;
		},
		minimumPaymentDue() {
			return this.profileData.minimumStudentLoanPayment;
		}
	},
	watch: {
		savingsSubcategories(value) {
			this.setSavingsSubcategories(value);
		},
		maxSavingsRemaining(savings) {
			if (savings === 0) {
				this.savingsSubcategoriesMax = this.savings.subcategories;
			} else {
				this.savingsSubcategoriesMax = this.savings.subcategories;
			}
		}
	},
	created() {
		if (this.categoryGroups) {
			this.setCategoryTabs();
		}
	},
	mounted() {
		if (this.savingsSubcategories) {
			this.setSavingsSubcategories(this.savingsSubcategories);
			this.updateSubcategoryDescriptions();
		}
		if (this.debtsSubcategories) {
			this.setDebtsSubcategories(this.debtsSubcategories);
		}
		if (this.profileData) {
			this.debtsEducation();
			this.studentLoan();
			this.debtsCreditCard();
			this.creditCardLoan();
		}

		this.useValidations = this.userData.studentPreviousLifeStageId !== null;

		this.$refs.button.$el.focus();
	},
	apollo: {
		cmsGoals: {
			query: gql`
				{
					cmsGoals {
						goal_ID
						name
						defaultName
						description
					}
				}
			`
		}
	},
	methods: {
		goPrevStep() {
			this.$emit('goPrevStep');
		},
		setSavingsSubcategories(subcategories) {
			this.savings.subcategories = subcategories;
			this.savings.amount = this.getTotalSavingsAmount(true);
		},
		setDebtsSubcategories(subcategories) {
			this.debts.subcategories = subcategories;
			this.debts.amount = this.getTotalDebtsAmount(true);
		},
		areAllBudgetsSet() {
			let subcategory = null;

			for (let i = 0, len = this.savings.subcategories.length; i < len; i++) {
				subcategory = this.savings.subcategories[i];
				if (subcategory.amount <= 0) {
					return false;
				}
				if (
					subcategory.hasOwnProperty('totalAmount') &&
					subcategory.totalAmount <= 0
				) {
					return false;
				}
			}

			for (let i = 0, len = this.debts.subcategories.length; i < len; i++) {
				subcategory = this.debts.subcategories[i];
				if (subcategory.budget <= 0) {
					return false;
				}
				if (
					subcategory.hasOwnProperty('totalAmount') &&
					subcategory.totalAmount <= 0
				) {
					return false;
				}
			}

			return true;
		},
		onSavingSliderChanged(subcategory) {
			// debugger;
			this.savings.amount = this.getTotalSavingsAmount(true);

			this.announceBudgetChange(
				subcategory.name,
				subcategory.amount,
				subcategory.minimum,
				subcategory.maximum
			);
		},
		onDebtSliderChanged(subcategory) {
			// debugger;
			const sumOfBudget = this.debts.subcategories.reduce(
				(sum, subcategory) => sum + subcategory.budget,
				0
			);
			this.debts.amount = sumOfBudget;

			this.announceBudgetChange(
				subcategory.name,
				subcategory.budget,
				subcategory.minimum,
				subcategory.maximum
			);
		},
		async announceBudgetChange(name, budget, minimum, maximum) {
			// debugger;
			this.$announcer.polite(
				`${this.$t('budgetSet', {
					name: name,
					amount: budget
				})} ${this.getWarningMessage(budget, minimum, maximum)}`
			);

			await this.announceDelay(200);
			this.announceBudgetLeft();
		},
		getWarningMessage(budget, minimum, maximum) {
			if (minimum && budget < minimum) {
				return this.$t('budgetMinWarning');
			} else if (maximum && budget > maximum) {
				return this.$t('budgetMaxWarning');
			}
			return '';
		},
		announceBudgetLeft() {
			// debugger;
			if (this.maxBudgetRemaining >= 0) {
				this.$announcer.polite(
					this.$t('budgetLeftToSpent', {
						amount: this.maxBudgetRemaining
					})
				);
			} else {
				this.announceError(this.$t('budgetUnsufficient'));
			}
		},
		announceTotalSavings(subcategory) {
			this.$announcer.polite(
				this.$t('totalSavingsAmountSet', {
					name: subcategory.name,
					amount: subcategory.totalAmount
				})
			);
		},
		toggleTooltip() {
			this.showTooltip = !this.showTooltip;
			this.showTooltipDesc = false;
			if (this.showTooltip) {
				this.$announcer.polite(this.$t('savingsTooltip'));
			} else {
				this.$announcer.polite('');
			}
		},
		toggleSliders(container, category) {
			category.expanded = !category.expanded;
		},
		async checkAndSaveBudget() {
			if (this.areAllBudgetsSet() && !this.noBudgetLeft) {
				this.requestSent = true;
				this.$announcer.polite(this.$t('loading'));
				const goalsIds = this.savings.subcategories.map(i => i.studentGoalId);

				this.budgetCheckData = await StudentsService.checkBudgetForStudent(
					this.userData.studentId,
					this.lifeStageData.lifeStage_ID,
					goalsIds
				);

				this.saveSavings();
			}
		},
		async updateStudentBudgetPopup() {
			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateStudent(update: $update) {
							id
						}
					}
				`,
				variables: {
					update: {
						id: this.userData.studentId,
						hideBudgetPopupOnHomeScreen: true
					}
				}
			});

			if (response.data) {
				this.$store.commit('updateUserData', {
					hideBudgetPopupOnHomeScreen: true
				});
				this.$emit('goNextStep');
			}
		},
		async saveSavings() {
			if (this.areAllBudgetsSet()) {
				const savingsBudgets = [...this.savings.subcategories];

				savingsBudgets.forEach(item => {
					delete item.name;
					delete item.accountBalance;
					delete item.minimum;
					delete item.maximum;
					delete item.description;
				});

				// TODO: Move this to separate file / function
				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation(
							$savingsBudgets: [StudentGoalSavingInputObjectGraphType]!
						) {
							addOrUpdateIgnoreNullValuesManyStudentGoalSaving(
								list: $savingsBudgets
							) {
								id
							}
						}
					`,
					variables: {
						savingsBudgets: savingsBudgets
					}
				});
				if (response.data) {
					this.saveDebtsBudget();
				}
			}
		},
		async saveDebtsBudget() {
			if (this.areAllBudgetsSet()) {
				const debtsBudgets = [...this.debts.subcategories];

				let lifeStage_ID;
				let studentId;
				let debtType;

				const alreadyCreatedDebts = {};

				this.budgetCheckData.debts.forEach(item => {
					alreadyCreatedDebts[item.debtType] = item;
				});

				debtsBudgets.forEach((item, index, array) => {
					const { budget } = item;

					lifeStage_ID = this.lifeStageData.lifeStage_ID;

					studentId = this.userData.studentId;
					if (item.name === debtNames.CREDIT_CARD) {
						debtType = debtTypes.CREDIT_CARD;
					} else if (item.name === debtNames.EDUCATION) {
						debtType = debtTypes.EDUCATION;
					}

					const debtToAdd = {
						budget,
						lifeStage_ID,
						studentId,
						debtType
					};

					const completedDebt = alreadyCreatedDebts[debtType];
					// if Item has already an Id - use it
					if (item.id) {
						debtToAdd.id = item.id;
					} else if (completedDebt && completedDebt.id) {
						// Otherwise check if the data is already presented in the BE
						// If it is - set that id which will  be resolved as Update
						debtToAdd.id = completedDebt.id;
					}

					array[index] = debtToAdd;
				});

				// TODO: Move this to spearate file / function
				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation($debtsBudgets: [StudentDebtBudgetInputObjectGraphType]!) {
							addOrUpdateIgnoreNullValuesManyStudentDebtBudget(
								list: $debtsBudgets
							) {
								id
							}
						}
					`,
					variables: {
						debtsBudgets: debtsBudgets
					}
				});

				if (response.data) {
					this.updateStudentBudgetPopup();
				}
			}
		},
		hasValidOptions(category) {
			// always true if we do not want to use validations
			if (!this.useValidations) {
				return true;
			}

			let key = 'budget';
			let useTotalAmount = false;
			// savings uses amount instead of budget
			if (category.name.toLowerCase() === this.$t('savings').toLowerCase()) {
				key = 'amount';
				useTotalAmount = true;
			}

			const t = category.subcategories.every(subcategory => {
				if (useTotalAmount) {
					return subcategory.totalAmount !== 0 && subcategory[key] !== 0;
				}
				return subcategory[key] !== 0;
			});

			return t;
		},
		validationClasses(value) {
			if (!this.useValidations) {
				return '';
			}
			value = value || 0;
			return value === 0 ? 'input--danger' : '';
		},
		getMaxSliderValue(percentageOfNmi) {
			if (percentageOfNmi) {
				return this.netMonthlyIncome * percentageOfNmi;
			}

			return this.netMonthlyIncome;

			// TODO: Use this after component is optimized
			// because this causes performance issues (visual lagging);

			// Add the value of the current category
			// since it is subtracted already in "maxBudgetRemaining"
			// If the first category is set to max (NMI), this will result
			// in MAX = 0 for each other category

			// return this.maxBudgetRemaining + value;
		},
		updateSubcategoryDescriptions() {
			const cmsGoals = this.cmsGoals;
			this.savings.subcategories.forEach((subcategory, index) => {
				cmsGoals.forEach(goal => {
					if (subcategory.name === goal.name) {
						this.savings.subcategories[index].description = goal.description;
					}
				});
			});
		},
		savingsGoalTooltip(index) {
			this.showTooltip = false;
			this.selectedSubcategoryIndex =
				this.selectedSubcategoryIndex === index ? -1 : index;

			if (this.selectedSubcategoryIndex !== -1) {
				this.showTooltipDesc = true;
			}
		},
		debtsEducation() {
			return this.$store.getters.debts.educationLoan;
		},
		debtsCreditCard() {
			return this.$store.getters.debts.creditCard;
		},
		studentLoan() {
			if (
				this.profileData.cmsLifeStage.displayOrder !== lifeStageOrders.TEENAGER
			) {
				this.showPostSecondary = true;
				this.educationLoan.loanTotal = this.debtsEducation().totalBalance.toLocaleString();
				this.educationLoan.outstandingAmount = this.debtsEducation().remainingBalance.toLocaleString();
				this.educationLoan.minimumPayment = this.debtsEducation().amount.toLocaleString();
			}
		},
		creditCardLoan() {
			if (
				this.profileData.cmsLifeStage.displayOrder !== lifeStageOrders.TEENAGER
			) {
				this.showCreditCard = true;
				this.creditCard.remainingBalance =
					`${this.JA_LOCALIZATION_IRL ? '€' : '$'}` +
					this.debtsCreditCard().balance.toLocaleString();
				this.creditCard.creditLimit =
					`${this.JA_LOCALIZATION_IRL ? '€' : '$'}` +
					this.debtsCreditCard().creditLimit.toLocaleString();
				this.creditCard.minimumPayment =
					`${this.JA_LOCALIZATION_IRL ? '€' : '$'}` +
					this.debtsCreditCard().amount.toLocaleString();
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.budget-creator-wrapper {
	width: 100%;
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	overflow: hidden;

	.text {
		&--small {
			font-size: 12px;
			vertical-align: middle;
			line-height: 24px;
		}

		&--danger {
			color: $font-red-alert;
		}
	}

	.input--danger {
		outline-color: $font-red-alert;
		outline-style: auto;
		outline-width: initial;
	}

	.budget-counter {
		margin-top: 8px;
		margin-bottom: $budget-counter-margin-bottom;
		color: $font-header;

		&.error {
			color: $font-red-alert;
		}
	}

	.scrollbar {
		flex-grow: 1;
	}

	.budget-creator {
		padding: 0 32px 24px 32px;
		max-height: $budget-creator-max-height;
		overflow-y: auto;
		overflow-x: hidden;

		@media screen and (min-width: 1280px) and (max-width: 1440px) {
			max-height: 26vh;
		}

		@media screen and (min-width: 1440px) {
			max-height: 35vh;
		}

		&.ireland {
			@media screen and (min-width: 1280px) and (max-width: 1440px) {
				max-height: $budget-creator-max-height;
			}

			@media screen and (min-width: 1440px) {
				max-height: $budget-creator-max-height;
			}
		}

		.category-cnt {
			width: 100%;
			background-color: $page-bgcolor;
			border-radius: 8px;
			margin-bottom: 8px;

			&.savings {
				.sliders-cnt {
					.slider-wrapper {
						&:not(:last-child) {
							border-bottom: 1px solid $container-border;
							padding-bottom: 24px;
							margin-top: 24px;
						}
					}
				}
			}

			&.expanded {
				.category-header {
					border-bottom: 1px solid $container-border;
					button {
						height: 24px;
						width: 24px;
					}
					svg {
						transform: rotate(270deg);
						transition: transform 0.5s linear;
					}
				}
			}

			&:hover {
				cursor: pointer;

				.category-header {
					button {
						height: 24px;
						width: 24px;
					}
					svg {
						fill: $hover-state;
					}
				}
			}

			.category-header {
				display: flex;
				align-items: center;
				padding: 24px 16px;
				width: 100%;
				background-color: transparent;
				border: 0;

				.heading-3 {
					flex-grow: 1;
					text-align: left;
					margin: 0;
					display: flex;
				}

				.heading-4 {
					margin: 0 16px;
				}
				button {
					height: 24px;
					width: 24px;
				}

				svg {
					transform: rotate(90deg);
					transition: transform 0.5s linear;
					width: 6px;
					height: 10px;
					fill: $primary-state;
				}
			}

			.sliders-cnt {
				margin: 0 32px 32px 32px;
				padding-bottom: 16px;
				position: relative;

				.legend-wrapper {
					margin-top: 16px;
				}

				.slider-wrapper {
					position: relative;
					padding-bottom: 16px;
					&:first-child {
						margin-top: 24px;
					}
					&:not(:first-child) {
						margin-top: 16px;
					}
					.slider-info {
						text-align: left;
						&-title {
							display: flex;
							gap: 5px;
						}

						.saving-amount-wrapper {
							width: 100%;
						}

						.body-text-2 {
							display: inline-block;
						}

						.tooltip-wrapper {
							position: relative;
							display: inline-block;

							.tooltip-button {
								background-color: transparent;
								border: 0;
								padding: 0;
								margin-left: 4px;

								::v-deep path {
									fill: $info-icon-color;
								}
							}

							.tooltip-msg {
								transform: translate(36px, -50%);
								top: 14px;
								left: 10px;

								@media (min-width: $breakpoint-mid) {
									width: 320px;
									font-size: 24px;
								}
							}

							svg {
								height: 24px;
								width: 24px;
								&:hover {
									cursor: pointer;
								}
							}
						}
					}

					.msg {
						width: 100%;
						position: absolute;
						bottom: 10px;
						text-align: right;

						&.min-msg {
							color: $budget-min-color;
						}

						&.max-msg {
							color: $budget-max-color;
						}
					}

					.slider-name {
						text-align: left;
						text-transform: capitalize;
						margin-bottom: 16px;
						display: block;
					}

					.body-text-2 {
						margin-bottom: 16px;
					}

					.budget-slider-wrapper {
						display: flex;

						.budget-slider {
							flex-grow: 1;
						}

						.budget-input {
							margin-left: 16px;
							margin-top: -8px;
							width: 80px;
						}
					}
				}
			}
		}
	}
	.student {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		padding: 20px;
		gap: 10px;
	}
	.legend {
		display: flex;
		.legend-color {
			height: 10px;
			width: 10px;
			border-radius: 100%;
			background-color: $budget-min-color;
			flex-shrink: 0;
			position: relative;
			top: 4px;
			margin-right: 8px;
		}

		.body-text-2 {
			text-align: left;
		}
	}

	.slide-enter {
		transform: scaleY(0);
	}

	.slide-enter-active {
		transition: transform 0.5s ease;
		transform-origin: top;
	}

	.slide-leave-to {
		transform: scaleY(0);
	}

	.slide-leave-active {
		transition: transform 0.3s ease;
		transform-origin: top;
	}
}
</style>
